import { IHttpClient } from './IHttpClient';
import FileSaver from 'file-saver';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { ErrorHandler } from './ErrorHandler';

const ERRORS_CODES_BOUNDARY = 299;

class HttpClient implements IHttpClient {
  private _abortController: AbortController;
  private _headers: Headers;
  private _signal: AbortSignal;
  private readonly _baseURL: string;

  constructor(headers: Headers, baseURL: string) {
    this._abortController = new AbortController();
    this._headers = headers;
    this._signal = this._abortController.signal;
    this._baseURL = baseURL;
  }

  public abort(): void {
    this._abortController.abort();
  }

  public buildURL(
    path: string,
    values: Record<string, unknown> | null
  ): string {
    let URL = `${this._baseURL}/${path}`;
    if (values) {
      const query = Object.keys(values)
        .map((key: string) => `${key}=${values[key as keyof typeof values]}`)
        .join('&');
      if (query.length > 0) {
        URL += `?${query}`;
      }
    }
    return URL;
  }

  get(
    path: string,
    values: Record<string, unknown> | null = null,
    customAbortSignal?: AbortSignal
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.makeCall('GET', path, values, customAbortSignal)
        .then(resolve)
        .catch(reject);
    });
  }

  post(path: string, data: BodyInit | null): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.makeCallWithData('POST', path, data).then(resolve).catch(reject);
    });
  }

  put(path: string, data: BodyInit | null = null): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.makeCallWithData('PUT', path, data).then(resolve).catch(reject);
    });
  }

  delete(
    path: string,
    values: Record<string, unknown> | null
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.makeCall('DELETE', path, values).then(resolve).catch(reject);
    });
  }

  downloadFile(
    path: string,
    fileName: string,
    values: Record<string, unknown> | null = null
  ): Promise<unknown> {
    const URL = this.buildURL(path, values);
    return new Promise((resolve, reject) => {
      fetch(URL, {
        method: 'GET',
        signal: this._signal,
        headers: this._headers,
      })
        .then((response) => {
          if (response.status > ERRORS_CODES_BOUNDARY) {
            ErrorHandler(response.status);
            reject(response);
          } else {
            response.blob().then((blob) => {
              FileSaver.saveAs(blob, fileName);
            });
            resolve(response);
          }
        })
        .catch(reject);
    });
  }

  downloadFileWithNameHeader(
    path: string,
    values: Record<string, unknown> | null = null
  ): Promise<unknown> {
    const URL = this.buildURL(path, values);
    return new Promise((resolve, reject) => {
      fetch(URL, {
        method: 'GET',
        signal: this._signal,
        headers: this._headers,
      })
        .then((response) => {
          if (response.status > ERRORS_CODES_BOUNDARY) {
            ErrorHandler(response.status);
            reject(response);
          } else {
            response.blob().then((blob) => {
              const contentDisposition = response.headers.get(
                'Content-Disposition'
              );
              const filename = contentDisposition
                ? contentDisposition.split('filename=')[1].split('"').join('')
                : 'file';

              FileSaver.saveAs(blob, filename);
            });
            resolve(response);
          }
        })
        .catch(reject);
    });
  }

  makeCall(
    method: string,
    path: string,
    values: Record<string, unknown> | null = null,
    abortSignal?: AbortSignal
  ): Promise<unknown> {
    const URL = this.buildURL(path, values);

    return new Promise((resolve, reject) => {
      fetch(URL, {
        method,
        signal: abortSignal ? abortSignal : this._signal,
        headers: this._headers,
      })
        .then((result) => {
          if (result.status > ERRORS_CODES_BOUNDARY) {
            ErrorHandler(result.status);
            reject(result);
          } else {
            result.json().then(resolve);
          }
        })
        .catch(reject);
    });
  }

  makeCallWithData(
    method: string,
    path: string,
    data: BodyInit | null
  ): Promise<unknown> {
    const URL = this.buildURL(path, null);
    const body = data ? data : undefined;

    return new Promise((resolve, reject) => {
      fetch(URL, {
        method,
        body,
        signal: this._signal,
        headers: this._headers,
      })
        .then((result) => {
          if (result.status > ERRORS_CODES_BOUNDARY) {
            reject(result);
          } else {
            result
              .text()
              .then((data) => resolve(data ? JSON.parse(data) : data));
          }
        })
        .catch(reject);
    });
  }
}

export default HttpClient;
